/* eslint-disable camelcase */
/* eslint-disable consistent-return */
import { combineEpics, ofType } from 'redux-observable';
import { REHYDRATE } from 'redux-persist';
import { empty, EMPTY, from, interval, of } from 'rxjs';
import {
  catchError,
  debounce,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
} from 'rxjs/operators';

import { getOrderLeverage } from 'actions/orderbook';
import { resetAllPagination } from 'actions/pagination';
import {
  contractTypeFilter,
  getFills,
  getOpenOrdersFromApi,
  getOpenStopOrdersFromApi,
  getOrdersHistory,
  getPositionsFromApi,
  getTradePreferences,
  getWithdrawalTradingCredits,
  withHoldingFilters,
  withoutContractType,
} from 'actions/trade';
import {
  getAppliedPromos,
  getKycStatus,
  getReferralData,
  setSHA256EmailHex,
  getUserProfile,
  isAuthenticated,
  setUnauthorized,
  checkIfAuthTokenExpired,
} from 'actions/user';
import { getBalances, getConversionConfig } from 'actions/wallet';
import { LoginViaBiometricsActionTypes, LoginViaQrActionTypes } from 'actionTypes/auth';
import { OFFRAMP_ACTION_TYPES } from 'actionTypes/offramp';
import ORDER_BOOK from 'actionTypes/orderbook';
import { TAB_REACTIVE } from 'actionTypes/other';
import TRADE, { TRADE_CONSTANTS } from 'actionTypes/trade';
// eslint-disable-next-line import/order
import USER, {
  CHECK_IF_AUTH_TOKEN_EXPIRED,
  NATIVE_USER_DATA,
  USER_CONNECTED,
} from 'actionTypes/user';

import WALLET from 'actionTypes/wallet';
import routes from 'constants/routes';
import { pipe } from 'helpers/ramda';
import { isAuthTokenExpired } from 'helpers/user';
import { isMobileOrTablet, isOptions } from 'helpers/utils';
import {
  selectedProductIdSelector,
  selectedProductSelector,
} from 'selectors/tradeSelectors';

import { getProductByIDHelper } from './alerts';

const getSelectedProductLeverage = () => {
  return (dispatch, getState) => {
    const selectedProduct = selectedProductSelector(getState());
    if (
      selectedProduct &&
      selectedProduct.contract_type &&
      selectedProduct.contract_type !== 'spot'
    ) {
      pipe(getState, selectedProductIdSelector, getOrderLeverage, dispatch)();
    }
  };
};

const postOrderSuccessActions = [
  getSelectedProductLeverage,
  isMobileOrTablet() ? contractTypeFilter(getFills) : withHoldingFilters(getFills),
];

const postOrderCancelActions = [getBalances, getSelectedProductLeverage];

const postAuthActionsDegradedMode = [
  getBalances,
  getPositionsFromApi,
  getOpenOrdersFromApi,
  getOpenStopOrdersFromApi,
  getConversionConfig,
  getUserProfile,
  getKycStatus,
  setSHA256EmailHex,
];

const postAuthActionsLiveMode = [
  ...postAuthActionsDegradedMode,
  getAppliedPromos,
  getWithdrawalTradingCredits,
  getTradePreferences,
  getReferralData,
];

export const getTradePageActions = () => {
  const isTradePage = window.location.pathname.includes('/trade/');
  if (isTradePage) {
    return [
      isMobileOrTablet()
        ? contractTypeFilter(getFills)()
        : withHoldingFilters(getFills)(),
      isMobileOrTablet()
        ? contractTypeFilter(getOrdersHistory)()
        : withHoldingFilters(getOrdersHistory)(),
    ];
  }
  return [];
};

const isTransactionLogPage = () => {
  return window.location.pathname.includes('/transaction_logs/');
};

export const getTransactionPageAction = () => {
  if (isTransactionLogPage()) {
    return [withoutContractType(getFills), withoutContractType(getOrdersHistory)];
  }
  return [];
};

// TODO: Should I add getTradePreferences, getPendingDeposits, getWalletAddress??
const refreshDataActions = [() => resetAllPagination()];

const holdingsCalls = [
  withHoldingFilters(getFills),
  withHoldingFilters(getOrdersHistory),
];

// Delays each and every action in refreshDataActions array
// const refreshDataActions = _refreshDataActions.map(withDelay)

const trade = {
  [ORDER_BOOK.CHANGE_ORDER_LEVERAGE.SUCCESS]: [getBalances],
  [TRADE.WITHDRAWAL_TRADING_CREDITS_FORFEIT.SUCCESS]: [
    getBalances,
    getWithdrawalTradingCredits,
  ],
  [OFFRAMP_ACTION_TYPES.SUBMIT_FIAT_WITHDRAWAL_CREATE.SUCCESS]: [
    getBalances,
    getWithdrawalTradingCredits,
  ],
  [WALLET.CONVERT_CURRENCY.SUCCESS]: [getBalances],
  [WALLET.TRANSFER_FUNDS_SPOT.SUCCESS]: [getBalances],
  [TRADE.ORDER.SUCCESS]: postOrderSuccessActions,
  [TRADE.ORDER_CANCEL.SUCCESS]: postOrderCancelActions,
  [USER_CONNECTED]: refreshDataActions,
  [TRADE.WALLET_BALANCE_TOP_UP.SUCCESS]: [getBalances],
  [TAB_REACTIVE]: refreshDataActions,
};

const authTrade = {
  [LoginViaQrActionTypes.SET_AUTHORIZED_USER]: postAuthActionsLiveMode,
  [LoginViaBiometricsActionTypes.SET_AUTHORIZED_USER]: postAuthActionsLiveMode,
  [USER.LOGIN.SUCCESS]: postAuthActionsLiveMode,
  [USER.LOGINMFA.SUCCESS]: postAuthActionsLiveMode,
  [USER.LOGIN_EMAIL_VERIFICATION.SUCCESS]: postAuthActionsLiveMode,
  [USER.PHONE_OTP_VERIFICATION.SUCCESS]: postAuthActionsLiveMode,
  [REHYDRATE]: [checkIfAuthTokenExpired],
  [NATIVE_USER_DATA]: postAuthActionsLiveMode,
};

const aTYPES = Object.keys(authTrade);
export const authTradeEpic = (action$, state$) =>
  action$.pipe(
    ofType(...aTYPES),
    filter(() => isAuthenticated(state$.value.user)),
    map(action => {
      if (action.fromSync || action.key === 'optionsChain') {
        // rehydrate running on session storage persist also . added check to prevent multiple api calls
        return null;
      }
      return authTrade[action.type];
    }),
    mergeMap(postActions => {
      if (!postActions) {
        return empty();
      }
      const pathname = state$.value?.router?.location?.pathname || '';
      const isDegradedModeRoute = pathname.includes(routes.degradedManagePosition);

      // Use degraded mode actions if on degraded route OR if app_status is degraded_mode
      const actions =
        isDegradedModeRoute || state$.value.settings.app_status === 'degraded_mode'
          ? postAuthActionsDegradedMode
          : postAuthActionsLiveMode;
      return of(...actions).pipe(
        map(pAction => pAction()),
        catchError(err => console.error('DEBUG:\n', err))
      );
    })
  );

const TYPES = Object.keys(trade);
export const tradeEpic = (action$, state$) =>
  action$.pipe(
    ofType(...TYPES),
    filter(() => isAuthenticated(state$.value.user)),
    map(action => {
      if (action.fromSync) {
        return null;
      }
      return trade[action.type];
    }),
    mergeMap(postActions => {
      if (!postActions) {
        return empty();
      }
      return of(...postActions).pipe(
        map(pAction => pAction()),
        catchError(err => console.error('DEBUG:\n', err))
      );
    })
  );

const selectedOrderHistory = () => {
  // NOTE: Commented for future reference
  // const filterType = state.holdings.holdingsFilterType;
  // if (filterType === 'selected') {
  //   return [
  //     withSelectedProductFilterType(getFills),
  //     withSelectedProductFilterType(getOrdersHistory),
  //   ];
  // }
  return holdingsCalls;
};

export const holdingsFilterTypeEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      TRADE_CONSTANTS.CHANGE_HOLDINGS_FILTER_TYPE,
      TRADE_CONSTANTS.CHANGE_HOLDINGS_CONTRACT_TYPE
    ),
    filter(
      action =>
        isAuthenticated(state$.value.user) &&
        !isTransactionLogPage() &&
        !action?.payload?.skipApiCall
    ),
    debounce(i => interval(200 * i)),
    distinctUntilChanged(),
    mergeMap(() =>
      from(selectedOrderHistory(state$.value)).pipe(
        map(pAction => pAction()),
        catchError(err => console.error('DEBUG:\n', err))
      )
    )
  );

export const zeroFeeOptionsOfferEpic = (action$, state$) => {
  return action$.pipe(
    ofType(TRADE.ORDER.SUCCESS),
    map(action => {
      try {
        const { payload } = action;
        const { product_id: productId, side } = payload;
        const product = getProductByIDHelper(state$.value, productId);

        const { contract_type: contractType } = product;
        const isOptionsProduct = isOptions(contractType);

        return isOptionsProduct && side === 'buy' ? [getAppliedPromos] : [];
      } catch (error) {
        return [];
      }
    }),
    mergeMap(postActions => {
      if (!postActions) {
        return EMPTY;
      }
      return of(...postActions).pipe(
        map(pAction => pAction()),
        catchError(err => console.error('DEBUG:\n', err))
      );
    })
  );
};

const validateAuthTokenExpiry = (action$, state$) =>
  action$.pipe(
    ofType(CHECK_IF_AUTH_TOKEN_EXPIRED),
    mergeMap(() => {
      if (isAuthTokenExpired(state$.value.user)) {
        return of(setUnauthorized());
      }

      const pathname = state$.value?.router?.location?.pathname || '';
      const isDegradedModeRoute = pathname.includes(routes.degradedManagePosition);

      // Use degraded mode actions if on degraded route OR if app_status is degraded_mode
      const postAuthActions =
        isDegradedModeRoute || state$.value.settings.app_status === 'degraded_mode'
          ? postAuthActionsDegradedMode
          : postAuthActionsLiveMode;
      const postAuthActionTypes = postAuthActions.map(postAuthAction => postAuthAction());
      return from(postAuthActionTypes);
    })
  );

export default combineEpics(
  validateAuthTokenExpiry,
  authTradeEpic,
  tradeEpic,
  holdingsFilterTypeEpic,
  zeroFeeOptionsOfferEpic
);
